import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, GripVertical, Mail, Users } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  onePagersService,
  ConciergeProviderOnePager,
} from '../services/onePagersService';

// Copy from the Claude Design prototype (op-prototype.jsx). The modal is dual-mode:
// with no peers selected it behaves like today's single-provider send; selecting a
// peer switches it to the peer model (project intro + per-provider blurbs).
const PROVIDER_PLACEHOLDER = '{SERVICE PROVIDER ACCOUNT NAME}';

function buildSingleBodyTemplate(providerName: string): string {
  const name = providerName.trim() || PROVIDER_PLACEHOLDER;
  return [
    `<p>{INSERT BRIEF PROVIDER DESCRIPTION FOLLOWED BY MOST RELEVANT FIT POINTS - I.E. number of successful engagements performed for other BluWave PE/Portco clients, high level description of most relevant project, relevant industry experience points, engagement feedback (ratings or qualitative feedback, etc)}</p>`,
    `<p><br></p>`,
    `<p>Next Steps: Upon review, please let us know what your availability looks like to meet with ${name}. If you have any questions about ${name} to inform your selection decision, please let us know, and we'll make sure they're addressed during the intro call.</p>`,
  ].join('');
}

// Provider-agnostic intro used when more than one provider is included (design copy).
const MULTI_INTRO_TEMPLATE = [
  `<p>Based on your project needs, we've put together the following providers for your review. A brief description and one-pager for each is included below.</p>`,
  `<p>Next Steps: Upon review, please let us know which providers you'd like to meet with and what your availability looks like. If you have any questions to inform your selection decision, let us know and we'll address them during the intro calls.</p>`,
].join('');

const BLURB_TEMPLATE =
  '<p>{Brief provider description + most relevant fit points — successful engagements for other BluWave PE/Portco clients, most relevant project, industry experience, engagement feedback}</p>';

function hasPlaceholderText(html: string): boolean {
  return html.includes('{INSERT ') || html.includes('{Brief provider') || html.includes(PROVIDER_PLACEHOLDER);
}

function isQuillEmpty(html: string): boolean {
  return html.replace(/<[^>]*>/g, '').trim() === '';
}

// Salesforce IDs come in 15- and 18-character forms (the 18-char form is the 15-char
// form plus a 3-char checksum), and the launch context and SOQL don't always agree on
// which. Compare on the 15-char prefix so the primary provider is reliably excluded
// from the peer list.
const sameSfId = (a?: string, b?: string): boolean =>
  !!a && !!b && a.slice(0, 15) === b.slice(0, 15);

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const isReady = (p: ConciergeProviderOnePager) => p.published && p.hasPdf;

const byName = (a: ConciergeProviderOnePager, b: ConciergeProviderOnePager) =>
  a.providerName.localeCompare(b.providerName);

// Seeds the initial arrangement: includable (published + PDF) providers lead, so an
// unpublished one-pager never outranks one that's ready to send. Ties fall back to name to
// keep the order stable across loads. The user reorders from here by dragging.
const byReadiness = (a: ConciergeProviderOnePager, b: ConciergeProviderOnePager) =>
  Number(isReady(b)) - Number(isReady(a)) || byName(a, b);

/** Wrapper mirroring the drag pattern already in RelevantProjectExperience.tsx. */
function SortableBlurbCard({
  id,
  children,
}: {
  id: string;
  children: (handleProps: React.HTMLAttributes<HTMLElement>) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : undefined,
    position: 'relative',
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ ...attributes, ...listeners })}
    </div>
  );
}

/** One provider's description card. The drag handle is absent for the pinned primary. */
function BlurbCard({
  card,
  value,
  onChange,
  readOnly,
  handleProps,
}: {
  card: { id: string; name: string; isPrimary: boolean };
  value: string;
  onChange: (value: string) => void;
  readOnly: boolean;
  handleProps?: React.HTMLAttributes<HTMLElement>;
}) {
  return (
    <div
      className="mb-2.5 overflow-hidden rounded-[10px] border border-[#E2E2E2] bg-white last:mb-0"
      data-testid={`blurb-card-${card.id}`}
    >
      <div className="flex items-center gap-2 border-b border-[#E2E2E2] bg-[#EDF1F4] px-3.5 py-2.5 text-[14px] font-bold text-[#132543]">
        {handleProps ? (
          <span
            {...handleProps}
            role="button"
            tabIndex={0}
            aria-label={`Drag to reorder ${card.name}`}
            data-testid={`blurb-drag-${card.id}`}
            className="-ml-1 flex cursor-grab touch-none text-[#9AA3B2] active:cursor-grabbing"
          >
            <GripVertical className="h-4 w-4" />
          </span>
        ) : (
          // Keeps the primary card's title aligned with the draggable ones below it.
          <span aria-hidden className="-ml-1 w-4" />
        )}
        <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-[#132543] text-[11px] font-bold tracking-[0.02em] text-white">
          {initialsOf(card.name)}
        </span>
        {card.name}
        {card.isPrimary && (
          <span className="ml-auto rounded-full bg-[#BDE3FF] px-[9px] py-[3px] text-[10.5px] font-bold uppercase tracking-[0.04em] text-[#1e7bb0]">
            Primary
          </span>
        )}
      </div>
      <div className="[&_.ql-container]:min-h-[64px] [&_.ql-container]:border-0 [&_.ql-container]:text-[13.5px] [&_.ql-editor]:min-h-[64px] [&_.ql-toolbar]:border-x-0 [&_.ql-toolbar]:border-t-0 [&_.ql-toolbar]:bg-white">
        <ReactQuill
          theme="snow"
          value={value}
          onChange={onChange}
          modules={QUILL_MODULES}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
}

export type ProjectSendMode = 'single' | 'project';

interface ProjectSendEmailModalProps {
  accountId: string;
  introductionId: string;
  versionId: string;
  conciergeId: string;
  providerName?: string;
  onClose: () => void;
  // Receives which send path ran: 'single' (one provider) or 'project' (peer bundle).
  onSuccess: (mode: ProjectSendMode) => void;
}

export default function ProjectSendEmailModal({
  accountId,
  introductionId,
  versionId,
  conciergeId,
  providerName = '',
  onClose,
  onSuccess,
}: ProjectSendEmailModalProps) {
  const [intro, setIntro] = useState(() => buildSingleBodyTemplate(providerName));
  // The default template swaps single<->multi until the user edits the editor.
  const [introDirty, setIntroDirty] = useState(false);
  // Keyed by introductionId. Entries survive uncheck so drafts are preserved on re-check;
  // inclusion is determined solely by selectedPeerIds.
  const [blurbs, setBlurbs] = useState<Record<string, string>>(() => ({
    [introductionId]: BLURB_TEMPLATE,
  }));
  const [selectedPeerIds, setSelectedPeerIds] = useState<Set<string>>(new Set());
  // Explicit display order for every blurb card — the primary included — by introductionId.
  // Seeded from the fetch order (readiness) and rewritten by a drag. Ids that are currently
  // unchecked stay in the array so their position survives an uncheck/re-check, matching how
  // blurb drafts are preserved.
  const [cardOrder, setCardOrder] = useState<string[]>([]);
  const [primaryProvider, setPrimaryProvider] = useState<ConciergeProviderOnePager | null>(
    null
  );

  const [peers, setPeers] = useState<ConciergeProviderOnePager[] | null>(null);
  const [peersError, setPeersError] = useState<string | null>(null);
  const [peersReloadKey, setPeersReloadKey] = useState(0);
  const [peersOpen, setPeersOpen] = useState(false);

  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!conciergeId) {
      setPeersError('Missing concierge id — cannot load other providers.');
      return;
    }
    const controller = new AbortController();
    setPeers(null);
    setPeersError(null);
    onePagersService
      .listConciergeProviders(conciergeId, { signal: controller.signal })
      .then((res) => {
        // The peers list (checkboxes) excludes the primary, but the primary's own record is
        // kept so it can be dragged like any other card (BPP-249). Includable (published +
        // PDF) providers lead (design #4).
        const primary =
          res.providers.find((p) => sameSfId(p.introductionId, introductionId)) ?? null;
        const others = res.providers.filter((p) => !sameSfId(p.introductionId, introductionId));
        others.sort(byReadiness);
        setPrimaryProvider(primary);
        setPeers(others);
        // Primary leads the initial arrangement as before; a sort or drag can move it.
        setCardOrder([introductionId, ...others.map((p) => p.introductionId)]);
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setPeersError(e instanceof Error ? e.message : 'Failed to load other providers.');
      });
    return () => controller.abort();
  }, [conciergeId, introductionId, peersReloadKey]);

  // The primary's own record, always keyed by the introductionId this modal was opened with
  // so blurb keys and the send payload stay consistent with the prop even if Salesforce
  // returned the other id form. Falls back to a synthetic record if the response omitted it.
  const primaryRecord: ConciergeProviderOnePager = useMemo(
    () => ({
      accountId: '',
      tagline: '',
      published: true,
      hasPdf: true,
      latestPublishedVersionId: null,
      sfStatus: null,
      latestPublishedAt: null,
      ...(primaryProvider ?? {}),
      introductionId,
      providerName: providerName || primaryProvider?.providerName || 'This provider',
    }),
    [primaryProvider, introductionId, providerName]
  );

  const isPrimaryId = (id: string) => sameSfId(id, introductionId);

  // Card and payload order follow the displayed list order. cardOrder is the source of
  // truth; anything newly checked that isn't in it yet lands at the end.
  const orderedCards = useMemo(() => {
    const pool = new Map<string, ConciergeProviderOnePager>();
    pool.set(primaryRecord.introductionId, primaryRecord);
    (peers ?? []).forEach((p) => pool.set(p.introductionId, p));
    const included = (id: string) => isPrimaryId(id) || selectedPeerIds.has(id);

    const ordered = cardOrder
      .map((id) => pool.get(id))
      .filter((p): p is ConciergeProviderOnePager => !!p && included(p.introductionId));
    const placed = new Set(ordered.map((p) => p.introductionId));
    const unplaced = Array.from(pool.values()).filter(
      (p) => included(p.introductionId) && !placed.has(p.introductionId)
    );
    return [...ordered, ...unplaced];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peers, primaryRecord, cardOrder, selectedPeerIds, introductionId]);

  const includedPeers = useMemo(
    () => orderedCards.filter((p) => !isPrimaryId(p.introductionId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderedCards, introductionId]
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const visible = orderedCards.map((p) => p.introductionId);
    const oldIndex = visible.indexOf(String(active.id));
    const newIndex = visible.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(visible, oldIndex, newIndex);
    // Rebuild the full order, dropping the moved ids back in against the unchecked ones.
    const remaining = cardOrder.filter((id) => !reordered.includes(id));
    setCardOrder([...reordered, ...remaining]);
  };
  const multi = includedPeers.length > 0;
  const includedCount = 1 + includedPeers.length;

  const togglePeer = (peer: ConciergeProviderOnePager) => {
    setSelectedPeerIds((prev) => {
      const next = new Set(prev);
      const willBeMulti = next.has(peer.introductionId) ? next.size - 1 > 0 : true;
      if (next.has(peer.introductionId)) {
        next.delete(peer.introductionId);
      } else {
        next.add(peer.introductionId);
        setBlurbs((b) =>
          b[peer.introductionId] === undefined ? { ...b, [peer.introductionId]: BLURB_TEMPLATE } : b
        );
      }
      // Swap the default template when crossing the single<->multi boundary,
      // but never clobber content the user has edited.
      if (!introDirty) {
        setIntro(willBeMulti ? MULTI_INTRO_TEMPLATE : buildSingleBodyTemplate(providerName));
      }
      return next;
    });
  };

  const includedEditors = multi
    ? [intro, ...orderedCards.map((p) => blurbs[p.introductionId] ?? '')]
    : [intro];
  const hasPlaceholders = includedEditors.some(hasPlaceholderText);
  const hasEmptyEditor = multi && includedEditors.some(isQuillEmpty);
  const sendDisabled = sending || hasPlaceholders || hasEmptyEditor;
  const sendDisabledReason = hasPlaceholders
    ? 'Placeholder text detected — please remove before sending'
    : hasEmptyEditor
      ? 'The intro and every included blurb need content before sending'
      : undefined;

  const handleSend = async () => {
    setError(null);
    setSending(true);
    try {
      if (!multi) {
        // Single provider — today's real send flow.
        await onePagersService.sendEmail(accountId, introductionId, versionId, {
          conciergeId,
          emailBody: isQuillEmpty(intro) ? undefined : intro,
        });
        onSuccess('single');
      } else {
        await onePagersService.sendProjectEmail(accountId, introductionId, versionId, {
          conciergeId,
          emailIntro: intro,
          // Displayed order, primary wherever the user ranked it. The backend identifies
          // the primary by id rather than position, so this order is the email's order.
          providers: orderedCards.map((p) => ({
            introductionId: p.introductionId,
            blurb: blurbs[p.introductionId] ?? '',
          })),
        });
        onSuccess('project');
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const blurbCards: Array<{ id: string; name: string; isPrimary: boolean }> = orderedCards.map(
    (p) => ({
      id: p.introductionId,
      name: p.providerName,
      isPrimary: isPrimaryId(p.introductionId),
    })
  );

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-6"
      style={{ background: 'rgba(19,37,67,0.45)' }}
      onMouseDown={onClose}
    >
      <div
        className="max-h-[90vh] w-[660px] max-w-full overflow-y-auto rounded-[14px] bg-white px-[30px] pb-6 pt-7"
        style={{ boxShadow: '0 24px 60px rgba(19,37,67,0.28)' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 className="m-0 mb-2 font-['Inter',sans-serif] text-[21px] font-bold text-[#132543]">
          Send One-Pager Email
        </h2>
        <p className="m-0 mb-[18px] text-[14px] leading-[1.55] text-[#29446D]">
          Recipients will get a project update email with {multi ? 'these one-pagers as PDF versions' : 'this one-pager as a PDF version'} as
          well as {multi ? 'direct links' : 'a direct link'} to view {multi ? 'them' : 'it'} in their platform.
        </p>

        <div className="mb-5 flex items-center gap-2.5 rounded-[8px] bg-[#BDE3FF] px-3.5 py-3 text-[13.5px] text-[#132543]">
          <svg className="h-[18px] w-[18px] shrink-0 text-[#4BABE0]" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <span data-testid="included-count-banner">
            {multi ? (
              <>
                <b>{includedCount} one-pagers</b> will be included in this email.
              </>
            ) : (
              'A PDF of this one-pager will be included in the email.'
            )}
          </span>
        </div>

        <label className="mb-2 block text-[12px] font-bold uppercase tracking-[0.03em] text-[#60667C]">
          {multi ? 'Email Intro' : 'Email Body'}
        </label>
        <div className="overflow-hidden rounded-[10px] border border-[#E2E2E2] [&_.ql-container]:min-h-[190px] [&_.ql-container]:text-sm [&_.ql-editor]:min-h-[190px] [&_.ql-toolbar]:bg-[#EDF1F4]">
          <ReactQuill
            theme="snow"
            value={intro}
            onChange={(value) => {
              setIntro(value);
              setIntroDirty(true);
            }}
            modules={QUILL_MODULES}
            readOnly={sending}
          />
        </div>

        {multi && (
          <div className="mt-5">
            <div className="mb-2">
              <label className="block text-[12px] font-bold uppercase tracking-[0.03em] text-[#60667C]">
                Provider Descriptions
              </label>
            </div>
            <p className="mb-3 mt-[-2px] text-[12.5px] text-[#60667C]">
              Each provider&apos;s description appears above their one-pager in the email. Drag
              to set the order — the primary provider can be ranked anywhere.
            </p>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={orderedCards.map((p) => p.introductionId)}
                strategy={verticalListSortingStrategy}
              >
                {blurbCards
                  .map((card) => (
                    <SortableBlurbCard key={card.id} id={card.id}>
                      {(handleProps) => (
                        <BlurbCard
                          card={card}
                          value={blurbs[card.id] ?? ''}
                          onChange={(value) => setBlurbs((b) => ({ ...b, [card.id]: value }))}
                          readOnly={sending}
                          handleProps={handleProps}
                        />
                      )}
                    </SortableBlurbCard>
                  ))}
              </SortableContext>
            </DndContext>
          </div>
        )}

        <div className="mt-[18px] border-t border-[#E2E2E2] pt-4">
          <button
            type="button"
            className="flex items-center gap-2 border-0 bg-transparent p-0 font-['Inter',sans-serif] text-[14px] font-bold text-[#132543]"
            onClick={() => setPeersOpen((v) => !v)}
          >
            <span
              className="flex text-[#60667C] transition-transform duration-150"
              style={{ transform: peersOpen ? 'none' : 'rotate(-90deg)' }}
            >
              <ChevronDown size={16} />
            </span>
            <Users size={18} className="text-[#4BABE0]" />
            Include other providers on this concierge
            {selectedPeerIds.size > 0 && (
              <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#4BABE0] px-[5px] text-[11px] font-bold text-white">
                {selectedPeerIds.size}
              </span>
            )}
          </button>

          {peersOpen && (
            <div className="mt-3.5 overflow-hidden rounded-[10px] border border-[#E2E2E2]">
              <div className="grid grid-cols-[1fr_130px] border-b border-[#E2E2E2] bg-[#EDF1F4] px-4 py-[9px]">
                <span className="text-[11px] font-bold uppercase tracking-[0.04em] text-[#60667C]">Provider</span>
                <span className="text-center text-[11px] font-bold uppercase tracking-[0.04em] text-[#60667C]">One-Pager</span>
              </div>
              {peers === null && !peersError && (
                <p className="m-0 px-4 py-3 text-sm text-[#60667C]">Loading providers…</p>
              )}
              {peersError && (
                <p className="m-0 px-4 py-3 text-sm text-red-600">
                  {peersError}{' '}
                  <button type="button" className="underline" onClick={() => setPeersReloadKey((k) => k + 1)}>
                    Retry
                  </button>
                </p>
              )}
              {peers !== null && peers.length === 0 && (
                <p className="m-0 px-4 py-3 text-sm text-[#60667C]">No other providers in this project.</p>
              )}
              {peers !== null &&
                peers.map((peer) => {
                  const selectable = peer.published && peer.hasPdf;
                  return (
                    <label
                      key={peer.introductionId}
                      className={
                        'grid grid-cols-[22px_1fr_130px] items-center gap-3 border-b border-[#E2E2E2] px-4 py-3 last:border-b-0 ' +
                        (selectable ? 'cursor-pointer hover:bg-[#BDE3FF]/40' : 'cursor-not-allowed')
                      }
                    >
                      <input
                        type="checkbox"
                        className="h-[17px] w-[17px] accent-[#4BABE0]"
                        checked={selectedPeerIds.has(peer.introductionId)}
                        disabled={!selectable || sending}
                        onChange={() => selectable && togglePeer(peer)}
                      />
                      <span className="flex min-w-0 flex-col">
                        <span className={'text-[14px] font-bold ' + (selectable ? 'text-[#132543]' : 'text-[#60667C]')}>
                          {peer.providerName}
                        </span>
                        {peer.tagline && (
                          <span className={'text-[12.5px] ' + (selectable ? 'text-[#29446D]' : 'text-[#60667C]')}>
                            {peer.tagline}
                          </span>
                        )}
                      </span>
                      <span
                        className={
                          'inline-flex items-center justify-center gap-1.5 justify-self-center whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-bold ' +
                          (peer.published ? 'bg-[#B0E9D6] text-[#2f7d5e]' : 'bg-[#EEEEEE] text-[#60667C]')
                        }
                      >
                        {peer.published ? (
                          <>
                            <Check size={13} /> Published
                          </>
                        ) : (
                          'Not published'
                        )}
                      </span>
                    </label>
                  );
                })}
            </div>
          )}
        </div>

        {error && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="sticky bottom-[-24px] mt-[22px] flex justify-end gap-2.5 border-t border-[#E2E2E2] bg-white pb-2 pt-4">
          <button
            type="button"
            className="rounded-[8px] border border-[#E2E2E2] bg-white px-[18px] py-2.5 font-['Inter',sans-serif] text-[13.5px] font-bold text-[#29446D] transition-colors hover:border-[#29446D] disabled:cursor-not-allowed disabled:opacity-45"
            onClick={onClose}
            disabled={sending}
          >
            Cancel
          </button>
          <span title={sendDisabledReason}>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-[8px] bg-[#132543] px-[18px] py-2.5 font-['Inter',sans-serif] text-[13.5px] font-bold text-white transition-colors hover:bg-[#0e1d36] disabled:cursor-not-allowed disabled:opacity-45"
              onClick={handleSend}
              disabled={sendDisabled}
            >
              <Mail size={15} />
              {sending ? 'Sending…' : 'Send Email'}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}
